import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Field } from 'formik';
import type React from 'react';

export interface InputFieldProps {
  id?: string;
  label?: string | any;
  extra?: React.ReactNode;
  placeholder?: string;
  type?: string;
  name: string;
  errors?: string;
  touched?: boolean;
  isRequired?: boolean;
  full?: boolean;
  rightElement?: React.ReactNode;
  fontSize?: any;
  placeholderFontColor?: string;
  placeholderFontSize?: string;
  placeholderFontWeight?: string;
  extraInfo?: React.ReactNode;
  labelFontSize?: string;
  labelMb?: string;
  isDisabled?: boolean;
  hideErrorMessage?: boolean;
  rightElementStyles?: any;
  groupStyles?: any;
  tooltipElement?: React.ReactNode;
  [x: string]: any;
}

export function InputField(props: InputFieldProps) {
  const {
    id,
    label,
    extra,
    errors,
    touched,
    placeholder,
    type,
    mb,
    isRequired,
    name,
    validate,
    full,
    rightElement,
    minW,
    maxW,
    fontSize,
    placeholderFontColor,
    placeholderFontWeight,
    placeholderFontSize,
    extraInfo,
    labelFontSize,
    labelMb,
    isDisabled,
    hideErrorMessage,
    rightElementStyles,
    groupStyles,
    tooltipElement,
    onDark,
    ...rest
  } = props;

  const [lightColor, darkColor] = onDark
    ? ['white', 'chakra-body-text']
    : ['chakra-body-text', 'white'];

  const textColorPrimary = useColorModeValue(lightColor, darkColor);
  const isError = !!(errors && errors.length > 0);
  return (
    <Flex
      direction="column"
      mb={mb ?? '30px'}
      width={full ? 'full' : 'unset'}
      minW={minW}
      maxW={maxW}
    >
      <FormControl isRequired={isRequired} isInvalid={isError} isDisabled={isDisabled}>
        {label && (
          <HStack justifyContent="space-between">
            <FormLabel
              display="flex"
              htmlFor={id}
              fontSize={labelFontSize || '2xl'}
              fontWeight="bold"
              lineHeight="normal"
              color={textColorPrimary}
              mb={labelMb || 4}
              _hover={{ cursor: 'pointer' }}
            >
              {label}
              {extra && (
                <Text fontSize="sm" fontWeight="400" ms={2}>
                  {extra}
                </Text>
              )}
            </FormLabel>
            {tooltipElement && tooltipElement}
          </HStack>
        )}
        <InputGroup size="sm" position="relative" {...groupStyles}>
          <Field
            as={Input}
            name={name}
            type={type}
            id={id}
            bg="blue.800"
            border="3px solid white"
            borderRadius="10px"
            fontWeight="500"
            color={textColorPrimary}
            fontSize={fontSize ? fontSize : '20px'}
            variant="main"
            outline={isError && touched && '3px solid red'}
            placeholder={placeholder}
            _placeholder={{
              fontWeight: placeholderFontWeight ? placeholderFontWeight : '600',
              color: placeholderFontColor ? placeholderFontColor : 'gray.600',
              fontSize: placeholderFontSize ? placeholderFontSize : '20px',
            }}
            h="60px"
            maxH="60px"
            px={7}
            pr={rightElement ? '102px' : '34px'}
            {...rest}
          />
          {rightElement && (
            <InputRightElement mx="34px" h="full" {...rightElementStyles}>
              {rightElement}
            </InputRightElement>
          )}
        </InputGroup>
        {extraInfo}
        {!hideErrorMessage && isError && touched && (
          <FormErrorMessage ml="10px">{errors}</FormErrorMessage>
        )}
      </FormControl>
    </Flex>
  );
}
